import { useState, useEffect } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle2, XCircle, Loader2, Lock, PlugZap, Save } from "lucide-react";
import { toast } from "sonner";

const BASE_URLS = { sandbox: "https://test.spinpos.net", production: "https://spinpos.net" };
const blank = { tpn: "", registerId: "", authKey: "", iposApiKey: "", iposSecretKey: "", spinProxyUrl: "", spinProxyPort: "", spinBaseUrl: "", environment: "sandbox", deviceModel: "" };

export default function GatewaySettings() {
  const [rows, setRows] = useState([]);
  const [lotId, setLotId] = useState("");
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(null);
  const [result, setResult] = useState(null);
  const cur = rows.find((r) => r.lotId === lotId);

  useEffect(() => {
    api.get("/gateway/credentials").then(({ data }) => {
      setRows(data);
      if (data.length) setLotId((data.find((r) => r.configured) || data[0]).lotId);
    });
  }, []);

  useEffect(() => {
    setResult(null);
    if (!cur) return;
    setForm({
      ...blank,
      spinProxyUrl: cur.spinProxyUrl || "",
      spinProxyPort: cur.spinProxyPort ?? "",
      spinBaseUrl: cur.spinBaseUrl || BASE_URLS.sandbox,
      environment: cur.environment || "sandbox",
      deviceModel: cur.deviceModel || "",
    });
  }, [lotId, rows]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setBusy("save");
    try {
      const payload = { ...form, spinProxyPort: form.spinProxyPort === "" ? null : Number(form.spinProxyPort) };
      const { data } = await api.put(`/gateway/credentials/${lotId}`, payload);
      setRows((rs) => rs.map((r) => (r.lotId === lotId ? data : r)));
      toast.success(`Gateway credentials saved for ${data.lotName}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally {
      setBusy(null);
    }
  };

  const test = async () => {
    setBusy("test");
    try {
      const { data } = await api.post(`/gateway/credentials/${lotId}/test`);
      setResult(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Test failed");
    } finally {
      setBusy(null);
    }
  };

  return (
    <Card data-testid="gateway-settings" className="border-slate-800 bg-slate-900 p-6 lg:col-span-2">
      <div className="mb-1 flex items-center gap-2">
        <Lock className="h-5 w-5 text-[#d946ef]" />
        <h2 className="font-heading text-xl font-bold text-slate-100">Payment Gateway — Dejavoo SPIn Cloud</h2>
      </div>
      <p className="mb-5 text-sm text-slate-400">
        Credentials are stored per lot, encrypted on the server and never shown again. Leave a secret field blank to keep the saved value.
      </p>

      <div className="mb-5 flex flex-wrap items-end gap-4">
        <div className="w-72 space-y-2">
          <Label className="text-slate-300">Lot</Label>
          <Select value={lotId} onValueChange={setLotId}>
            <SelectTrigger data-testid="gateway-lot-select" className="h-11 border-slate-700 bg-slate-950"><SelectValue /></SelectTrigger>
            <SelectContent>
              {rows.map((r) => (
                <SelectItem key={r.lotId} value={r.lotId} data-testid={`gateway-lot-option-${r.lotId}`}>
                  {r.lotName}{r.configured ? " · configured" : ""}{r.lotActive ? "" : " (not live)"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {cur && (
          <span data-testid="gateway-status" className={`rounded-full px-3 py-1 text-xs font-bold ${cur.configured ? "bg-emerald-500/15 text-emerald-400" : "bg-slate-800 text-slate-400"}`}>
            {cur.configured ? `Configured · ${cur.environment}` : "Not configured"}
          </span>
        )}
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Secret label="TPN" k="tpn" masked={cur?.tpnMasked} form={form} set={set} />
        <Secret label="Register ID" k="registerId" masked={cur?.registerIdMasked} form={form} set={set} />
        <Secret label="Auth Key" k="authKey" masked={cur?.authKeyMasked} form={form} set={set} />
        <Secret label="iPOS Transact API Key (auto-charge)" k="iposApiKey" masked={cur?.iposApiKeyMasked} form={form} set={set} />
        <Secret label="iPOS Transact Secret Key (auto-charge)" k="iposSecretKey" masked={cur?.iposSecretKeyMasked} form={form} set={set} />
        <Text label="SPIn Proxy URL" k="spinProxyUrl" form={form} set={set} placeholder="test.spin.spinpos.net" />
        <Text label="SPIn Proxy Port" k="spinProxyPort" form={form} set={set} type="number" placeholder="48777" />
        <Text label="Device Model" k="deviceModel" form={form} set={set} placeholder="QD2" />
        <div className="space-y-2">
          <Label className="text-slate-300">Environment</Label>
          <Select value={form.environment} onValueChange={(v) => setForm((f) => ({ ...f, environment: v, spinBaseUrl: BASE_URLS[v] }))}>
            <SelectTrigger data-testid="gateway-environment" className="h-11 border-slate-700 bg-slate-950"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="sandbox">Sandbox</SelectItem>
              <SelectItem value="production">Production</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2">
          <Text label="SPIn Sale Base URL" k="spinBaseUrl" form={form} set={set} placeholder={BASE_URLS[form.environment]} />
        </div>
      </div>

      <div className="mt-5 flex flex-wrap gap-3">
        <Button data-testid="gateway-save" onClick={save} disabled={!lotId || busy} className="h-11 gap-2 bg-[#A0148C] font-bold hover:bg-[#80106F]">
          {busy === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save Credentials
        </Button>
        <Button data-testid="gateway-test" onClick={test} disabled={!lotId || busy} variant="outline" className="h-11 gap-2 border-slate-700 bg-slate-950 font-bold text-slate-200 hover:bg-slate-800">
          {busy === "test" ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlugZap className="h-4 w-4" />} Test Connection
        </Button>
      </div>

      {result && (
        <div data-testid="gateway-test-result" className="mt-5 rounded-lg border border-slate-800 bg-slate-950 p-4">
          <p data-testid="gateway-test-summary" className={`mb-3 font-bold ${result.ok ? "text-emerald-400" : "text-red-400"}`}>
            {result.ok ? "All checks passed — no payment was sent." : "Some checks failed."}
          </p>
          <ul className="space-y-1.5 text-sm">
            {result.checks.map((c, i) => (
              <li key={c.name} data-testid={`gateway-check-${i}`} className="flex items-start gap-2">
                {c.ok ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" /> : <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-400" />}
                <span className="text-slate-200">{c.name}</span>
                <span className="text-slate-500">— {c.detail}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
}

function Secret({ label, k, masked, form, set }) {
  return (
    <div className="space-y-2">
      <Label className="text-slate-300">{label}</Label>
      <Input
        data-testid={`gateway-${k}`}
        type="password"
        autoComplete="new-password"
        value={form[k]}
        onChange={(e) => set(k, e.target.value)}
        placeholder={masked ? `Saved ${masked}` : "Not set"}
        className="h-11 border-slate-700 bg-slate-950 font-data"
      />
    </div>
  );
}

function Text({ label, k, form, set, type = "text", placeholder }) {
  return (
    <div className="space-y-2">
      <Label className="text-slate-300">{label}</Label>
      <Input
        data-testid={`gateway-${k}`}
        type={type}
        value={form[k]}
        onChange={(e) => set(k, e.target.value)}
        placeholder={placeholder}
        className="h-11 border-slate-700 bg-slate-950 font-data"
      />
    </div>
  );
}
